import { useCallback, useEffect, useState, useRef, useMemo } from 'react'
import { useActiveChatId, useChatMessages, useChatOnboardingStep } from '../../../../store'
import { messageStyles } from '../nested/message-markdown'
import { useActions, useAuth } from '../../../../providers'
import { twMerge } from 'tailwind-merge'
import { useWordTypewriter } from './hooks/useTypewriter'
import { useOnboardingMessages } from './hooks/useDummyChatMessages'
import { motion } from 'framer-motion'
import { ChatRequestBody, useSubmitChatRequest, useSubmitMessage } from '../../../../hooks'
import { GlobalState } from '@highlight/ipc'
import { appIconUrl } from '@renderer/utils/app-icon'
import { HighlightLogo } from '@highlight/ui'

export const DummyMessages = () => {
  const { getScreenshotSources, focusWindow } = useActions()
  const chatOnboardingStep = useChatOnboardingStep()
  const activeChatId = useActiveChatId()
  const messages = useChatMessages(activeChatId)
  const submitChatRequest = useSubmitChatRequest()
  const submitMessage = useSubmitMessage()
  const { onboardingMessages, isLoading } = useOnboardingMessages(chatOnboardingStep as 'name' | 'use-case')
  const { displayedText: initialText, isComplete: initialComplete } = useWordTypewriter(
    onboardingMessages?.welcomeMessage || '',
    9,
  )

  const [hasSelectedSuggestion, setHasSelectedSuggestion] = useState(false)
  const [suggestions, setSuggestions] = useState<any[]>([])
  const [windowSources, setWindowSources] = useState<any[]>([])

  const { getAuthToken } = useAuth()
  const hasFetchedSuggestions = useRef(false)

  const disableSuggestions = useMemo(() => {
    return messages.length > 0 || hasSelectedSuggestion
  }, [messages, hasSelectedSuggestion])

  // Animation constants to prevent object recreation
  const wordAnimationProps = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    transition: { duration: 0.3 },
  }

  const suggestionAnimationProps = {
    initial: { opacity: 0, y: 10 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5 },
  }

  const optionAnimationProps = {
    initial: { opacity: 0, x: -20 },
    animate: { opacity: 1, x: 0 },
    transition: { duration: 0.3, ease: [0.25, 0.1, 0.25, 1] },
  }

  const appIconContainerStyle = {
    borderRadius: '12px',
    background: 'rgba(255, 255, 255, 0.05)',
  }

  const appIconStyle = {
    fill: '#FFF',
    filter: 'drop-shadow(0 0.5px 1px rgba(0, 0, 0, 0.15))',
  }

  useEffect(() => {
    if (hasFetchedSuggestions.current || disableSuggestions) return

    const fetchSuggestions = async () => {
      hasFetchedSuggestions.current = true
      GlobalState.set('onboarding-suggestions', [])

      try {
        const accessToken = await getAuthToken()
        const sources = await getScreenshotSources(true)
        setWindowSources(sources.windows)

        const chatRequestBody: ChatRequestBody = {
          prompt: "Suggest 4 use cases for Highlight. Just use the tool, don't include any other text.",
          conversationId: undefined,
          additionalTools: [
            {
              name: 'create_suggestions',
              description:
                'Come up with a personalized and hyper-specific list of 4 suggestions for Highlight for them based on the apps/windows they have open. Avoid suggesting Highlight functionality they have not been able to use yet like meeting notes (meeting notes in Notion/another app are fair game though).' +
                '\nHere are the apps they have open: ' +
                (sources.windows
                  ?.map((window) => JSON.stringify({ appName: window.appName, windowTitle: window.windowTitle }))
                  .join('\n') || []),
              parameters: {
                type: 'object',
                properties: {
                  suggestions: {
                    type: 'array',
                    description:
                      'A personalized and specific list of 4 singular suggestions for the user based on their open windows, that they can use Highlight with right now. Prioritize how useful the suggestion would be for them if they sent it and a screenshot of their window to an LLM.',
                    items: {
                      type: 'object',
                      properties: {
                        title: {
                          type: 'string',
                          description: 'A short title for the suggestion. Max 2-3 words. Make it hyper relevant.',
                        },
                        appsToUse: {
                          type: 'array',
                          description:
                            'The 1 app window the user would use Highlight with for this suggestion. Only include a single window.',
                          items: {
                            type: 'object',
                            properties: { appName: { type: 'string' }, windowTitle: { type: 'string' } },
                            required: ['appName', 'windowTitle'],
                          },
                        },
                        suggestion: {
                          type: 'string',
                          description:
                            "A prompt to use Highlight with. Keep it short and very concise. It should be a single sentence. No more than 10 words. Make it hyper relevant, but avoid mentioning exact details that you get from the titles you receive (so it doesn't feel intrusive).",
                        },
                      },
                      required: ['title', 'suggestion', 'appsToUse'],
                    },
                  },
                },
                required: ['suggestions'],
              },
            },
          ],
          useMemory: false,
          useKnowledge: false,
          modelId: '8021d572-d47a-4f55-99bf-71814e139ed2',
          // TODO: Handle timezone
          ephemeral: true,
        }

        submitChatRequest(chatRequestBody, { accessToken, localConversationId: '' }, (parsedEventData) => {
          switch (parsedEventData.type) {
            case 'toolUse':
              try {
                const response = JSON.parse(parsedEventData.input as string)
                setSuggestions(response.suggestions)
              } catch (error) {
                console.error('Error processing suggestions response:', error)
              }
          }
        })
      } catch (error) {
        console.error('Error fetching suggestions:', error)
        hasFetchedSuggestions.current = false // Reset on error to allow retry
      }
    }

    fetchSuggestions()
  }, [getAuthToken, getScreenshotSources, submitChatRequest, disableSuggestions])

  useEffect(() => {
    window.ampltd.trackEvent('Chat Onboarding Step', {
      step: chatOnboardingStep,
      state: 'Reached',
    })
  }, [chatOnboardingStep])

  const onSelectSuggestion = useCallback(
    async (suggestion: any) => {
      if (disableSuggestions) return

      setHasSelectedSuggestion(true)

      window.ampltd.trackEvent('Onboarding Flow', {
        step: 'Dummy Chat Suggestion',
        action: 'Select Use Case',
        state: 'Completed',
        useCase: suggestion.title,
      })

      window.ampltd.trackEvent('Onboarding Suggestion Selected', {
        suggestionTitle: suggestion.title,
        suggestionText: suggestion.suggestion,
        appsToUse: suggestion.appsToUse.map((app: any) => app.appName),
        windowTitles: suggestion.appsToUse.map((app: any) => app.windowTitle),
        totalSuggestions: suggestions?.length || 0,
        suggestionIndex: suggestions?.findIndex((s: any) => s.title === suggestion.title) || 0,
      })

      const windowSource = windowSources.find(
        (window) =>
          window.appName === suggestion.appsToUse[0].appName &&
          window.windowTitle === suggestion.appsToUse[0].windowTitle,
      )
      await focusWindow?.(windowSource?.windowTitle, windowSource?.pid)
      setTimeout(() => {
        submitMessage(suggestion.suggestion, {
          requestCurrentContext: true,
          waitForContext: true,
        })
      }, 1000)
    },
    [suggestions, windowSources, focusWindow, submitMessage],
  )

  const chatMessageClassName = 'whitespace-pre-line text-sm font-normal leading-[24px] tracking-[-0.195px]'

  if (isLoading) {
    return null
  }

  const chooseTaskMessage = (
    <div
      className={twMerge(
        messageStyles({
          role: 'assistant',
          sequential: 'default',
        }),
        'border-border mb-4 min-h-[32vh] pb-4 pt-8',
        messages.length > 0 && 'border-b',
      )}
    >
      <HighlightHeader />
      <>
        <div className={twMerge(chatMessageClassName)}>
          {initialText.map((word, index) => (
            <motion.span key={index} {...wordAnimationProps}>
              {word + ' '}
            </motion.span>
          ))}
        </div>
        {initialComplete && (
          <motion.div
            className={twMerge(
              'my-2 flex flex-col gap-2 font-normal leading-[200%] tracking-[-0.195px]',
              disableSuggestions && 'opacity-30',
            )}
          >
            {suggestions?.slice(0, 3).map((suggestion: any, index: number) => (
              <motion.div
                {...suggestionAnimationProps}
                transition={{ ...suggestionAnimationProps.transition, delay: index * 0.2 }}
                id={`suggestion-${index}`}
                onAnimationComplete={() => {
                  // scroll into view
                  const element = document.getElementById(`suggestion-${index}`)
                  if (element) {
                    element.scrollIntoView({ behavior: 'smooth' })
                  }
                }}
                key={suggestion.title}
                className={twMerge(
                  `bg-light-3 hover:bg-light-5 relative flex h-full flex-row gap-3 rounded-lg p-2.5 ${disableSuggestions ? 'cursor-default' : 'cursor-pointer'}`,
                  disableSuggestions && 'hover:bg-light-3',
                )}
                onClick={() => onSelectSuggestion(suggestion)}
              >
                <div
                  className="w-19 h-19 shadow-xs flex aspect-square flex-shrink-0 items-center justify-center rounded-xl p-1"
                  style={appIconContainerStyle}
                >
                  {suggestion.appsToUse.slice(0, 1).map((app: any, index: number) => (
                    <img
                      key={index}
                      src={appIconUrl(app.appName)}
                      alt={app.appName}
                      className="aspect-square h-12 w-12 object-contain"
                      style={appIconStyle}
                    />
                  ))}
                </div>
                <div className="flex flex-1 flex-col items-start justify-start gap-0.5">
                  <div className="text-foreground select-none pt-1 text-[15px] font-medium leading-[1.5] tracking-[-0.195px]">
                    {suggestion.title}
                  </div>
                  <div className="text-foreground-secondary select-none text-[13px] font-normal leading-[165%] tracking-[-0.195px]">
                    {suggestion.suggestion}
                  </div>
                </div>
              </motion.div>
            )) || ''}
          </motion.div>
        )}
      </>
    </div>
  )

  return chooseTaskMessage
}

const HighlightHeader = () => (
  <div className={'flex items-center gap-3'}>
    <HighlightLogo size={16} tone="light" aria-hidden>
      <HighlightLogo.Icon />
    </HighlightLogo>
    <span className="text-xs font-medium leading-[21px]">Highlight</span>
  </div>
)
